import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { createTrackComment } from "../api";
import { CreateCommentRequest, LeadTrackCommentDTO } from "../dtos";
import { LEAD_TRACK_ACTIVITY_QUERY_KEY } from "./use-lead-track-activity.hook";
import { ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";

interface UseCreateTrackCommentOptions {
  trackUId: string;
  onSuccess?: (data: LeadTrackCommentDTO) => void;
  onError?: (error: unknown) => void;
}

export const useCreateTrackComment = ({
  trackUId,
  onSuccess,
  onError,
}: UseCreateTrackCommentOptions) => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<LeadTrackCommentDTO>,
    { response?: { data?: { message?: string } } },
    CreateCommentRequest
  >({
    mutationFn: (data) => createTrackComment(trackUId, data),
    onSuccess: (response) => {
      // Invalidate activity query to refresh the feed
      queryClient.invalidateQueries({ 
        queryKey: [LEAD_TRACK_ACTIVITY_QUERY_KEY, trackUId] 
      });

      showToast({
        type: ToastType.SUCCESS,
        message: "Comment added",
        toastId: "create-track-comment-success",
        options: {
          position: "bottom-left",
          autoClose: 2000,
        },
      });

      onSuccess?.(response.data);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to add comment";
      
      showToast({
        type: ToastType.ERROR,
        message: errorMessage,
        toastId: "create-track-comment-error",
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });

      onError?.(error);
    },
  });
};

export default useCreateTrackComment;

